import {useEffect, useId, useRef, useState, type FormEvent} from 'react';
import {approvalLabels, parseNativeRequest, validateNativeResponse, type NativeRequest, type NativeQuestionValue} from '../src/native-request';
import {ErrorNotice} from './components';
import {errorDetails} from './lib/store';
import type {ErrorDetails} from './lib/types';

type Draft = {text: string; selected: string[]; skipped: boolean};
type Props = {
  request: Record<string, unknown>;
  busy: boolean;
  answer: (requestId: string, result: Record<string, unknown>) => Promise<unknown>;
  cancel: (requestId: string, message: string) => Promise<unknown>;
};

function serialize(question: NativeQuestionValue, draft: Draft | undefined): string | null | undefined {
  if (!draft) return undefined;
  if (draft.skipped) return null;
  if (!question.multi) return draft.text.trim() ? draft.text : undefined;
  const answers = [...draft.selected, ...(draft.text.trim() ? [draft.text] : [])];
  return answers.length ? JSON.stringify(answers) : undefined;
}

function SavedAnswer({question, value}: {question: NativeQuestionValue; value: string | null}) {
  if (value === null) return <p className="field-help">Skipped.</p>;
  if (question.multi && value.trim().startsWith('[')) {
    try {
      const items: unknown = JSON.parse(value);
      if (Array.isArray(items) && items.every(item => typeof item === 'string')) {
        return <ul>{items.map((item, index) => <li key={index}>{item}</li>)}</ul>;
      }
    } catch { /* Show the saved native answer unchanged when it is not a JSON list. */ }
  }
  return <p style={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{value}</p>;
}

export function NativeQuestion(props: Props) {
  let parsed: NativeRequest;
  try {parsed = parseNativeRequest(props.request);}
  catch (error) {return <InvalidRequest {...props} error={errorDetails(error)}/>;}
  return <RequestForm key={parsed.id} {...props} parsed={parsed}/>;
}

function InvalidRequest({request, cancel, busy, error}: Props & {error: ErrorDetails}) {
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<ErrorDetails | null>(null);
  const lock = useRef(false);
  async function close() {
    if (busy || lock.current || typeof request.id !== 'string') return;
    lock.current = true; setPending(true);
    try {await cancel(request.id, `The request could not be displayed: ${error.message}`);}
    catch (error) {setFailure(errorDetails(error)); lock.current = false; setPending(false);}
  }
  return <section className="native-question"><ErrorNotice title="This request could not be shown" error={error}/>
    <ErrorNotice error={failure}/>{typeof request.id === 'string' && <button className="text-button" type="button"
      disabled={busy || pending} onClick={() => void close()}>Close request</button>}</section>;
}

function RequestForm({request, parsed, answer, cancel, busy}: Props & {parsed: NativeRequest}) {
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [selected, setSelected] = useState<string[]>(parsed.kind === 'setup' ? parsed.preselected : []);
  const [value, setValue] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState<ErrorDetails | null>(null);
  const lock = useRef(false), mounted = useRef(true);
  const formId = useId();
  useEffect(() => {mounted.current = true; return () => {mounted.current = false;};}, []);
  const disabled = busy || pending || sent;
  function update(questionId: string, changes: Partial<Draft>) {
    setDrafts(previous => ({...previous, [questionId]: {
      text: '', selected: [], skipped: false, ...previous[questionId], ...changes,
    }}));
  }
  async function submit(result?: Record<string, unknown>) {
    if (disabled || lock.current) return;
    lock.current = true; setPending(true); setFailure(null);
    try {
      if (result === undefined) await cancel(parsed.id, parsed.kind === 'unsupported' ? parsed.reason : 'The user closed this request.');
      else await answer(parsed.id, validateNativeResponse(request, result));
      if (mounted.current) {setValue(''); setIdentifier(''); setDrafts({}); setSelected([]); setSent(true);}
    } catch (error) {
      if (mounted.current) setFailure(errorDetails(error));
      lock.current = false;
    } finally {if (mounted.current) setPending(false);}
  }
  const editable = parsed.kind === 'clarify' ? parsed.questions.filter(question => !Object.hasOwn(parsed.locked, question.id)) : [];
  const complete = parsed.kind === 'clarify' ? editable.length > 0 && editable.every(question => serialize(question, drafts[question.id]) !== undefined)
    : parsed.kind === 'setup' ? selected.length > 0 || value.trim().length > 0
    : parsed.kind === 'login' ? identifier.trim().length > 0 && value.length > 0 : value.length > 0;
  function send(event: FormEvent) {
    event.preventDefault();
    if (!complete) return;
    if (parsed.kind === 'clarify') void submit({answers: Object.fromEntries(editable.map(question => [question.id, serialize(question, drafts[question.id])]))});
    else if (parsed.kind === 'setup') {
      const labels = selected.map(id => parsed.options.find(option => option.id === id)!.label);
      void submit({picked: selected.length ? parsed.multi ? selected : selected[0] : null,
        label: labels.length ? parsed.multi ? labels : labels[0] : null, said: value.trim() ? value : null});
    } else if (parsed.kind === 'login') void submit({value: JSON.stringify({identifier, password: value})});
    else if (parsed.kind === 'secret') void submit({value});
  }
  if (sent) return <section className="native-question" aria-live="polite"><p>Your response was sent.</p></section>;
  return <form className="native-question" onSubmit={send}>
    {parsed.kind === 'clarify' ? parsed.questions.map((question, index) => {
      const saved = Object.hasOwn(parsed.locked, question.id), draft = drafts[question.id], skipped = draft?.skipped === true;
      const textId = `${formId}-${index}`;
      return <fieldset key={question.id} disabled={disabled || saved}>
        <legend>{question.question}</legend>
        {saved ? <><p className="field-help">Your answer is saved.</p><SavedAnswer question={question} value={parsed.locked[question.id]!}/></> : <>
          {question.choices.length > 0 && <div className="native-question-choices">
            <p className="field-help">{question.multi ? 'Select all answers that apply. You can also add an answer.' : 'Select an answer or enter your own.'}</p>
            {question.choices.map((choice, choiceIndex) => <label className="native-question-choice" key={choiceIndex}>
              <input type={question.multi ? 'checkbox' : 'radio'} name={`${textId}-choices`} disabled={skipped}
                checked={question.multi ? draft?.selected.includes(choice) === true : draft?.text === choice}
                onChange={event => {
                  if (!question.multi) {update(question.id, {text: choice}); return;}
                  const current = draft?.selected ?? [];
                  update(question.id, {selected: event.target.checked ? [...new Set([...current, choice])] : current.filter(value => value !== choice)});
                }}/><span>{choice}</span></label>)}
          </div>}
          <label className="field" htmlFor={textId}><span>{question.multi ? 'Additional answer' : 'Your answer'}</span>
            <textarea id={textId} rows={3} disabled={skipped} value={draft?.text ?? ''} onChange={event => update(question.id, {text: event.target.value})}/></label>
          <label className="native-question-choice"><input type="checkbox" checked={skipped} onChange={event => update(question.id, {skipped: event.target.checked})}/><span>Skip this question</span></label>
        </>}
      </fieldset>;
    }) : parsed.kind === 'setup' ? <fieldset disabled={disabled}><legend>{parsed.question}</legend>
      {parsed.options.map(option => <label className="native-question-choice" key={option.id}>
        <input type={parsed.multi ? 'checkbox' : 'radio'} name={`${formId}-setup`} checked={selected.includes(option.id)}
          onChange={event => setSelected(current => parsed.multi ? event.target.checked ? [...new Set([...current, option.id])] : current.filter(id => id !== option.id) : [option.id])}/>
        <span>{option.label}{option.detail && <small className="field-help">{option.detail}</small>}</span></label>)}
      <label className="field"><span>{parsed.options.length ? 'Additional answer' : 'Your answer'}</span><textarea rows={3} value={value} onChange={event => setValue(event.target.value)}/></label>
    </fieldset> : parsed.kind === 'approval' ? <fieldset disabled={disabled}><legend>{parsed.title}</legend>
      {parsed.description && <p>{parsed.description}</p>}{parsed.command && <pre style={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{parsed.command}</pre>}
      {parsed.choices.map(choice => <button className="button" type="button" key={choice} onClick={() => void submit({choice})}>{approvalLabels[choice]}</button>)}
    </fieldset> : parsed.kind === 'secret' ? <fieldset disabled={disabled}><legend>{parsed.title}</legend><p>{parsed.prompt}</p>
      {parsed.detail && <p style={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{parsed.detail}</p>}
      <label className="field"><span>{parsed.code ? 'Verification code' : 'Private value'}</span><input type="password" autoComplete={parsed.code ? 'one-time-code' : 'off'}
        value={value} onChange={event => setValue(event.target.value)}/></label>
    </fieldset> : parsed.kind === 'login' ? <fieldset disabled={disabled}><legend>{parsed.title}</legend><p>{parsed.site}</p><p>{parsed.origin}</p>
      <label className="field"><span>Email or username</span><input type="text" autoComplete="off" value={identifier} onChange={event => setIdentifier(event.target.value)}/></label>
      <label className="field"><span>Password</span><input type="password" autoComplete="off" value={value} onChange={event => setValue(event.target.value)}/></label>
    </fieldset> : <ErrorNotice title={parsed.title} error={{name: 'UnsupportedHermesRequest', message: parsed.reason}}/>}
    <ErrorNotice error={failure}/>
    {parsed.kind === 'clarify' && editable.length === 0 && <p className="field-help">All answers are saved. Waiting for the agent to continue.</p>}
    {parsed.kind !== 'unsupported' && parsed.kind !== 'approval' && <button type="submit" className="button" disabled={disabled || !complete}>{pending ? 'Sending…' : 'Send answer'}</button>}
    {parsed.kind !== 'approval' && <button type="button" className="text-button" disabled={disabled} onClick={() => void submit()}>{parsed.kind === 'unsupported' ? 'Close request' : 'Cancel'}</button>}
  </form>;
}
